import { listsApi } from '../../api/lists-api';
import type { IListFieldCatalogue, ListFieldSource } from '../../api/lists-contracts';
import type { IMailPlaceholder, MailSurvey } from '../../api/mail-template-contracts';
import { ALL_SURVEYS, catalogueSections, fieldSourceNote } from '../../lists';
import type { IFieldSection, PickerField } from '../lists/field-picker';
import { OWN_GROUP, isOwnPlaceholder, placeholderWords } from './placeholder-text';
import { SURVEYS } from './template-fields';

/**
 * A template's placeholders in the Lists *Columns* sheet's groups (SR-523): survey-api builds a
 * template's field placeholders from the Lists field catalogue of the variant's survey, each naming
 * its field's code, so the picker reads that same catalogue and draws each field as Columns does —
 * its group (Record, Profile, Survey questions, Staff fields, Figures…), its name and question
 * number, where its value comes from — with the placeholder's sample beside it. A field an
 * administrator hid is not offered, but counted — by the catalogue, or by survey-api's `hidden`
 * where no catalogue is read; a catalogue field survey-api fills no placeholder for is left out.
 * The email's own placeholders come first, under This email.
 */

/** The Lists catalogue a variant's placeholders are drawn from: its survey's; for the base, the
 *  cross-survey one joined with the four surveys' — DD calls' *All surveys* — or the cross-survey
 *  one alone where a deployment does not hold all four. Null where none can be read (a reader
 *  without Lists), and the picker falls back on survey-api's groups. */
export async function readPlaceholderCatalogue(
  survey: Nullable<MailSurvey>,
  signal: AbortSignal
): Promise<Nullable<IListFieldCatalogue>> {
  const read = (code: string, surveys?: readonly string[]) =>
    listsApi.fields(code, signal, surveys).catch((error: unknown) => {
      if (signal.aborted) throw error;
      return null;
    });
  if (survey) return read(survey);
  return (
    (await read(
      ALL_SURVEYS.code,
      SURVEYS.map((entry) => entry.value)
    )) ?? read(ALL_SURVEYS.code)
  );
}

/** The picker's groups, with how many fields an administrator hid (`HiddenFieldsNote`). */
export type PlaceholderSections = IFieldSection[] & { hidden: number };

/** The own group's key, which the sheet opens. */
export const OWN_KEY = 'this-email';

/** A group's key, fit for an id. */
const keyOf = (title: string) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-');

/** A sample worth showing: not empty, and not the words beside it again. */
function said(text: string | undefined, ...beside: (string | undefined)[]): string | undefined {
  return text && !beside.includes(text) ? text : undefined;
}

/** A placeholder the catalogue does not place, as survey-api describes it. */
function ownField(placeholder: IMailPlaceholder): PickerField {
  const label = placeholderWords(placeholder);
  return {
    code: placeholder.name,
    label,
    number: null,
    description: said(placeholder.description, label, placeholder.label),
    sample: said(placeholder.sample, label, placeholder.label)
  };
}

/**
 * The placeholders as the picker's groups: This email; then the catalogue's groups in its order,
 * each field that has a placeholder drawn as the Columns sheet draws it; then any field placeholder
 * the catalogue does not hold, under survey-api's group — all of them while no catalogue is read,
 * but those survey-api marks `hidden`, which are counted. URL placeholders are a button's or a
 * link's address, never offered.
 */
export function placeholderSections(
  placeholders: readonly IMailPlaceholder[],
  catalogue: Nullable<IListFieldCatalogue>
): PlaceholderSections {
  const offered = placeholders.filter((placeholder) => placeholder.kind !== 'url');
  const own = offered.filter(isOwnPlaceholder);
  const byField = new Map(
    offered.flatMap((placeholder) => (!isOwnPlaceholder(placeholder) && placeholder.field ? [[placeholder.field, placeholder]] : []))
  );
  const sections: PlaceholderSections = Object.assign([], { hidden: 0 });
  if (own.length > 0) sections.push({ key: OWN_KEY, title: OWN_GROUP, fields: own.map(ownField) });

  const known = new Set(catalogue?.groups.flatMap((group) => group.fields.map((field) => field.code)) ?? []);
  const fromCatalogue = catalogueSections(catalogue);
  sections.hidden = fromCatalogue.hidden;
  for (const section of fromCatalogue) {
    // Profile is survey-api's `system` group, drawn apart.
    const source = (section.key === 'profile' ? 'system' : section.key) as ListFieldSource;
    const fields = section.fields.flatMap((field): PickerField[] => {
      const placeholder = byField.get(field.code);
      if (!placeholder) return [];
      return [
        {
          ...field,
          code: placeholder.name,
          description: field.provenance ? undefined : fieldSourceNote(field, source),
          sample: said(placeholder.sample, field.label)
        }
      ];
    });
    if (fields.length > 0) sections.push({ ...section, fields });
  }

  const rest = new Map<string, PickerField[]>();
  for (const [code, placeholder] of byField) {
    if (known.has(code)) continue;
    if (placeholder.hidden) {
      sections.hidden += 1;
      continue;
    }
    const group = placeholder.group ?? 'Mail';
    rest.set(group, [...(rest.get(group) ?? []), ownField(placeholder)]);
  }
  for (const [title, fields] of rest) sections.push({ key: `mail-${keyOf(title)}`, title, fields });
  return sections;
}

/**
 * The placeholders in the picker's order, each under its group's title and not hidden — the sheet
 * offers it — with those the picker does not offer (a hidden field's, a URL) after them, marked
 * `hidden` where an administrator hid the field: a pill naming one still reads.
 */
export function placeholdersInSheetOrder(
  placeholders: readonly IMailPlaceholder[],
  sections: readonly IFieldSection[]
): IMailPlaceholder[] {
  const byName = new Map(placeholders.map((placeholder) => [placeholder.name, placeholder]));
  const ordered = sections.flatMap((section) =>
    section.fields.flatMap((field) => {
      const placeholder = byName.get(field.code);
      return placeholder ? [{ ...placeholder, group: section.title, hidden: false }] : [];
    })
  );
  const placed = new Set(ordered.map((placeholder) => placeholder.name));
  const left = placeholders
    .filter((placeholder) => !placed.has(placeholder.name))
    .map((placeholder) => (placeholder.kind === 'url' ? placeholder : { ...placeholder, hidden: true }));
  return [...ordered, ...left];
}
